import React, { useState, useRef } from 'react';
import { useTemplates } from '@context/TemplateContext';
import { ImportStrategy } from '@/types/template.types';
import { X, Upload, Check, Download, Trash2 } from 'lucide-react';
import { Button, IconButton } from '../ui/Button';
import { Select } from '../ui/Form';
import { Badge } from '../ui/Badge';

interface TemplateManagerModalProps {
  onClose: () => void;
}

export const TemplateManagerModal: React.FC<TemplateManagerModalProps> = ({ onClose }) => {
  const { templates, exportAll, exportOne, importFromFile, clearAll } = useTemplates();
  const [selectedExportId, setSelectedExportId] = useState<string>('');
  const [importStrategy, setImportStrategy] = useState<ImportStrategy>('merge_skip');
  const [importResult, setImportResult] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [confirmClear, setConfirmClear] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (file: File) => {
    if (!file.name.endsWith('.json')) {
      setImportResult({ type: 'error', message: 'Please select a .json file' });
      return;
    }

    const result = await importFromFile(file, importStrategy);
    if (result.success) {
      setImportResult({
        type: 'success',
        message: `Successfully imported ${result.count} template${result.count !== 1 ? 's' : ''}`
      });
    } else {
      setImportResult({ type: 'error', message: result.error || 'Import failed' });
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleClearAll = () => {
    if (confirmClear) {
      clearAll();
      setConfirmClear(false);
      setImportResult({ type: 'success', message: 'All templates cleared' });
    } else {
      setConfirmClear(true);
      setTimeout(() => setConfirmClear(false), 4000);
    }
  };

  const strategyOptions = [
    { value: 'merge_skip', label: 'MERGE SKIP' },
    { value: 'merge_overwrite', label: 'MERGE OVERWRITE' },
    { value: 'replace_all', label: 'REPLACE ALL' },
  ];

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[200]" onClick={onClose}>
      <div className="bg-slate-900 border border-slate-800 rounded-3xl w-full max-w-lg flex flex-col shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
        <header className="px-6 py-4 border-b border-slate-800 flex items-center justify-between bg-slate-900/50">
          <h3 className="text-lg font-black text-slate-50 uppercase tracking-tighter flex items-center gap-2">
            ⚙️ Template Manager
          </h3>
          <IconButton
            variant="ghost"
            size="sm"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={20} />
          </IconButton>
        </header>

        <div className="p-6 space-y-8 overflow-y-auto max-h-[80vh] custom-scrollbar">
          {/* Export Section */}
          <section className="space-y-4">
            <h4 className="text-[11px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
              <Download size={14} /> Export Options
            </h4>

            <div className="space-y-3">
              <Button
                variant="secondary"
                size="md"
                fullWidth
                onClick={exportAll}
                disabled={templates.length === 0}
                className="text-accent text-xs uppercase tracking-widest rounded-xl"
              >
                Export All Templates ({templates.length})
              </Button>

              <div className="flex gap-2">
                <Select
                  size="md"
                  value={selectedExportId}
                  onChange={e => setSelectedExportId(e.target.value)}
                  disabled={templates.length === 0}
                  className="flex-grow rounded-xl"
                >
                  <option value="">-- Select single template --</option>
                  {templates.map(t => (
                    <option key={t.id} value={t.id}>{t.icon} {t.name}</option>
                  ))}
                </Select>
                <Button
                  variant="secondary"
                  size="md"
                  onClick={() => selectedExportId && exportOne(selectedExportId)}
                  disabled={!selectedExportId}
                  className="text-xs uppercase rounded-xl"
                >
                  Export
                </Button>
              </div>
            </div>
          </section>

          {/* Import Section */}
          <section className="space-y-4">
            <h4 className="text-[11px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
              <Upload size={14} /> Import Options
            </h4>

            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-[11px] text-slate-400 font-bold uppercase block tracking-wider">Duplicate Strategy</label>
                <div className="grid grid-cols-3 gap-2">
                  {strategyOptions.map(strategy => (
                    <Button
                      key={strategy.value}
                      variant={importStrategy === strategy.value ? 'outline' : 'ghost'}
                      size="xs"
                      onClick={() => setImportStrategy(strategy.value as ImportStrategy)}
                      className={`rounded-lg text-[11px] font-black uppercase tracking-tighter ${
                        importStrategy === strategy.value
                          ? 'bg-accent/10 border-accent text-accent'
                          : 'bg-slate-950 border border-slate-800 text-slate-400 hover:text-slate-400'
                      }`}
                    >
                      {strategy.label.replace('_', ' ')}
                    </Button>
                  ))}
                </div>
              </div>

              <div
                className={`border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all ${
                  isDragging ? 'bg-accent/5 border-accent' : 'bg-slate-950 border-slate-800 hover:border-slate-700'
                }`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".json"
                  onChange={e => e.target.files?.[0] && handleFileSelect(e.target.files[0])}
                  hidden
                />
                <Upload size={32} className={`mx-auto mb-3 ${isDragging ? 'text-accent' : 'text-slate-700'}`} />
                <p className="text-xs text-slate-400 font-medium">Drop .json here or click to browse</p>
              </div>

              {importResult && (
                <Badge
                  variant={importResult.type === 'success' ? 'success-muted' : 'danger-muted'}
                  size="md"
                  leftIcon={importResult.type === 'success' ? <Check size={14} /> : <X size={14} />}
                  className="w-full justify-start animate-in slide-in-from-top-2"
                >
                  {importResult.message}
                </Badge>
              )}
            </div>
          </section>

          {/* Danger Zone */}
          <section className="pt-6 border-t border-slate-800 space-y-4">
            <h4 className="text-[11px] font-black text-red-500 uppercase tracking-widest flex items-center gap-2">
              <Trash2 size={14} /> Danger Zone
            </h4>
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <Button
                variant={confirmClear ? 'danger' : 'ghost'}
                size="sm"
                onClick={handleClearAll}
                className={`w-full sm:w-auto text-[11px] font-black uppercase rounded-xl ${
                  !confirmClear ? 'bg-red-950/20 border border-red-900/30 text-red-500 hover:bg-red-950/40' : ''
                }`}
              >
                {confirmClear ? 'Click again to confirm' : 'Clear Library'}
              </Button>
              {templates.length > 0 && !confirmClear && (
                <Badge variant="default-muted" size="xs" className="uppercase">
                  Back up your templates first!
                </Badge>
              )}
            </div>
          </section>
        </div>

        <footer className="p-6 border-t border-slate-800 bg-slate-900/50 flex justify-end">
          <Button
            variant="secondary"
            size="sm"
            onClick={onClose}
            className="text-[11px] font-black uppercase rounded-xl"
          >
            Close
          </Button>
        </footer>
      </div>
    </div>
  );
};
